import { Button } from "plos/src/components/buttons/Button";
import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  summaryEditActions,
  summaryItem,
  summaryItemDisplay,
  summaryItemLabel,
  summaryItemText,
  summaryItemValue,
} from "../teamBuilder.css";

interface EditableSummaryItemProps {
  label: string;
  value: string;
  children: ReactNode;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
}

// Presentational summary row. Editing state is owned by the parent so that only
// one item can be edited at a time.
const EditableSummaryItem = ({
  label,
  value,
  children,
  isEditing,
  onEdit,
  onSave,
  onCancel,
}: EditableSummaryItemProps) => {
  const { t } = useTranslation("onboarding");

  const actionsRef = useRef<HTMLDivElement>(null);

  // Scroll the Save/Cancel buttons into view when this item starts editing so
  // they aren't hidden below the fold on small screens.
  useEffect(() => {
    if (isEditing) {
      actionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className={summaryItem}>
        {children}
        <div className={summaryEditActions} ref={actionsRef}>
          <Button appearance="error" size="small" onPress={onCancel}>
            {t("summary.cancel", "Cancel")}
          </Button>
          <Button size="small" onPress={onSave}>
            {t("summary.save", "Save")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={summaryItem}>
      <div className={summaryItemDisplay}>
        <div className={summaryItemText}>
          <span className={summaryItemLabel}>{label}:</span>
          <span className={summaryItemValue}>{value}</span>
        </div>
        <Button appearance="text" size="small" onPress={onEdit}>
          {t("summary.edit", "Edit")}
        </Button>
      </div>
    </div>
  );
};

export default EditableSummaryItem;
